import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import 'plyr/dist/plyr.css';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { ArticleJumpLinks, ArticleJumpLinksProps, Heading, Text } from '@atlassian/learning-components';
import { media } from '@atlaskit/primitives/responsive';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { HelmetContext } from 'src/components/Helmet';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { sendScreenEvent } from 'src/utils/analytics';
import { useReferrer } from 'src/hooks/useReferrer';
import { QuickFeedbackWrapper } from 'src/pages/LessonPage/children/QuickFeedbackWrapper';
import { getAssetUrl, isDevelopment } from 'src/services/http/user';
import { ContactUsSection } from 'src/components/ContactUsSection';
import { getContentLink, getContentLinkLabel, getContentLinkTarget } from 'src/pages/utils/utils';
import { AssociatedContent } from 'src/types/contentful/base-content';
import { ALM_INTEGRATION_FF } from 'src/constants';

import ArticleSection from './children/ArticleSection';
import { ArticleBreadcrumbs } from './children/ArticleBreadcrumbs';
import { ArticleLoaderProps } from './loader';
import { ShareArticleButton } from './children/ShareArticleButton';

export const ArticlePage: React.FC = () => {
  const helmet = useContext(HelmetContext);

  const { articleData } = useLoaderData() as ArticleLoaderProps;
  const { title, description } = articleData;

  const { user, isLoading, hasError } = useAtlassianUser();

  const { isInitialized } = useReferrer();

  const [isJumpLinksFixed, setIsJumpLinksFixed] = useState(false);
  const jumpLinksRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);

  const isAlmIntegrationEnabled = FeatureGates.checkGate(ALM_INTEGRATION_FF);

  helmet.setHelmet({
    title: `${articleData?.title} | Learning - Atlassian Community`,
    seoTags: articleData?.seo,
  });

  useEffect(() => {
    document.getElementsByTagName('html')[0].style.scrollPaddingTop = '136px';

    return () => {
      document.getElementsByTagName('html')[0].style.scrollPaddingTop = '0';
    };
  }, []);

  const handleScroll = useCallback(() => {
    if (jumpLinksRef.current) {
      const contentContainerTop = contentContainerRef.current?.offsetTop;
      const navHeight = 68;

      const scrollY = window.scrollY;
      const isScrolledPast = contentContainerTop ? scrollY >= contentContainerTop - navHeight : false;

      setIsJumpLinksFixed(isScrolledPast);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (!isLoading && !hasError && isInitialized) {
      sendScreenEvent(
        'articleScreen',
        {
          articleSlug: articleData.slug,
        },
        user?.account_id,
      );
    }
  }, [user, isLoading, hasError, isInitialized, articleData.slug]);

  // TODO: remove this once ALM integration is enabled
  if (!isDevelopment() && !isAlmIntegrationEnabled) {
    window.location.href = '/learning';

    return null;
  }

  const jumpLinks: ArticleJumpLinksProps['links'] = articleData?.sectionListCollection.items?.map((section) => ({
    label: section.title,
    href: `#${section.sys.id}`,
  }));

  const contactUsLink = articleData.contactUs?.contentLinksCollection?.items[0] as AssociatedContent;

  return (
    <main css={mainStyles} id="article-body" data-testid="article-page">
      <section
        css={headerOuterContainerStyles}
        style={
          { '--article-hero-banner': `url(${getAssetUrl('/images/contentBanners/articleHeroBanner.svg')})` } as Record<
            string,
            string
          >
        }
      >
        <div css={headerInnerContainerStyles}>
          <ArticleBreadcrumbs articleData={articleData} />
          <Heading level={1}>{title}</Heading>
          {description && <Text size="large">{description}</Text>}
          <div css={shareArticleStyles}>
            <ShareArticleButton />
          </div>
        </div>
      </section>
      <section css={contentContainerStyles}>
        <div
          ref={jumpLinksRef}
          css={[articleJumpLinksContainerStyles, isJumpLinksFixed && articleJumpLinksFixedStyles]}
        >
          <ArticleJumpLinks links={jumpLinks} />
        </div>
        <div css={articleSectionContainerStyles} ref={contentContainerRef}>
          {articleData?.sectionListCollection.items?.map((section) => (
            <ArticleSection key={section.sys.id} section={section} />
          ))}
          <div css={quickFeedbackContainerStyles}>
            <QuickFeedbackWrapper isNextItemAvailable={false} />
          </div>
        </div>
      </section>
      {contactUsLink && (
        <div>
          <ContactUsSection
            containerStyles={{ marginBottom: 0 }}
            title={articleData.contactUs.contentSectionTitle.contentSectionTitleExternal}
            subtitle={articleData.contactUs.contentSectionTitle.contentSectionSubtitle}
            linkUrl={getContentLink(contactUsLink) ?? ''}
            linkLabel={getContentLinkLabel(contactUsLink) ?? ''}
            linkTarget={getContentLinkTarget(contactUsLink)}
          />
        </div>
      )}
    </main>
  );
};

const mainStyles = css({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  paddingTop: '136px',

  '@media (max-width: 1279px)': {
    paddingTop: '0',
  },
});

const headerOuterContainerStyles = css({
  backgroundColor: '#F8F8F8',
  backgroundImage: 'var(--article-hero-banner)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
});

const headerInnerContainerStyles = css({
  maxWidth: '1022px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.150'),
  padding: `${token('space.600')} 0`,

  [media.below.md]: {
    padding: `${token('space.500')} ${token('space.300')}`,
  },

  [media.below.sm]: {
    padding: `${token('space.400')} ${token('space.200')}`,
  },

  // Breadcrumbs style
  '& li a': {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: '400',
    color: '#7D818A !important',
  },

  // Last breadcrumb style
  '& li:last-child a': {
    color: '#101214 !important',
  },
});

const contentContainerStyles = css({
  padding: '48px 0',

  [media.below.md]: {
    padding: '32px 0',
  },

  [media.below.sm]: {
    padding: '32px 0',
  },

  '& hr': {
    padding: 0,
    borderColor: token('color.border'),
    width: '100%',
  },

  '& div#paragraph-block': {
    paddingBottom: 0,
    margin: 0,
  },

  '& [data-testid="lesson-section-children"]': {
    display: 'flex',
    flexDirection: 'column',
    rowGap: token('space.200'),
  },

  '& div#paragraph-block a span': {
    color: token('color.link'),
  },

  '& div#paragraph-block span': {
    color: token('color.text'),
    fontFamily: 'Inter, sans-serif',
    fontSize: `16px !important`,
    lineHeight: '24px',
  },

  // button link text style
  '& [role="button"] span': {
    fontFamily: 'Charlie Text, sans-serif',
    fontWeight: token('font.weight.medium'),
    fontSize: `16px`,
  },

  // button link container
  '& [role="button"]': {
    padding: `${token('space.100')} ${token('space.150')} ${token('space.100')} ${token('space.100')}`,
  },
});

const articleSectionContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  rowGap: token('space.600'),

  [media.below.md]: {
    rowGap: token('space.400'),
  },

  // lesson section container style
  '& > div > div > div > div': {
    padding: '0',
    margin: '0',
  },
});

const quickFeedbackContainerStyles = css({
  margin: `${token('space.400')} auto`,
  width: '1022px',

  [media.below.md]: {
    width: '100%',
    padding: `0 ${token('space.300')}`,
  },

  [media.below.sm]: {
    padding: `0 ${token('space.200')} !important`,
  },
});

const articleJumpLinksContainerStyles = css({
  width: '100%',
  padding: `0 ${token('space.300')} ${token('space.400')} ${token('space.300')}`,
  pointerEvents: 'none',

  '& > div': {
    pointerEvents: 'all',
  },

  // desktop
  [media.above.md]: {
    margin: '0 auto',
    width: '1022px',
    display: 'flex',
    justifyContent: 'flex-end',
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: 0,
  },

  [media.below.sm]: {
    padding: `0 ${token('space.200')} ${token('space.400')} ${token('space.200')}`,
  },
});

const articleJumpLinksFixedStyles = css({
  // desktop
  [media.above.md]: {
    position: 'fixed',
    top: '68px', // sticky nav height
    zIndex: 1000,
  },
});

const shareArticleStyles = css({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: '20px', // 20px padding + 12px gap = 32px

  [media.above.md]: {
    display: 'none',
  },
});
